import React, { useMemo, useRef, useState } from "react";
import { localToday } from "../utils/localDate";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaFileExcel, FaSearch } from "react-icons/fa";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportControlBar from "../components/ReportControlBar";

import { API_ENDPOINTS } from "../config/constants";

const API_URL = API_ENDPOINTS.REPORT_CUSTOM_EXCEL;

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const DEFAULT_REPORT = {
  report_type: "order_wise",
  report_title: "",
  restaurant: "",
  fromdate: "",
  todate: "",
  row_count: 0,
  preview_count: 0,
  columns: [],
  preview_rows: [],
  summary: {},
  generated_at: "",
};

const normalizeColumn = (column, index) => {
  if (typeof column === "string") {
    return {
      id: `${column}-${index}`,
      key: column,
      label: column,
      type: "text",
    };
  }

  if (column && typeof column === "object") {
    const key = String(
      column.key || column.field || column.name || column.label || `column_${index}`
    );
    return {
      id: `${key}-${index}`,
      key,
      label: String(column.label || column.title || key),
      type: String(column.type || "text"),
    };
  }

  return {
    id: `column_${index}`,
    key: `column_${index}`,
    label: `Column ${index + 1}`,
    type: "text",
  };
};

const CustomExcelReportPage = () => {
  const [reportType, setReportType] = useState("order_wise");
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [report, setReport] = useState(DEFAULT_REPORT);
  const reportRef = useRef(null);

  const reportTypeOptions = [
    { value: "order_wise", label: "Order Wise" },
    { value: "date_wise", label: "Date Wise" },
    { value: "item_wise", label: "Item Wise" },
  ];

  const normalizedColumns = useMemo(
    () => (Array.isArray(report.columns) ? report.columns.map(normalizeColumn) : []),
    [report.columns]
  );

  const summaryEntries = useMemo(
    () =>
      Object.entries(report.summary || {}).filter(
        ([, value]) => value !== null && value !== undefined && value !== ""
      ),
    [report.summary]
  );

  const formatSummaryValue = (value) => {
    if (typeof value === "number") {
      return value.toLocaleString("en-IN", {
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
      });
    }
    return String(value);
  };

  const fetchReport = async ({ includeFile = false } = {}) => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      return;
    }

    const from = fromDate <= toDate ? fromDate : toDate;
    const to = fromDate <= toDate ? toDate : fromDate;
    if (includeFile) {
      setExporting(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: user.id,
          report_type: reportType,
          fromdate: from,
          todate: to,
          include_file: includeFile,
        }),
      });

      const raw = await response.text();
      let result = {};
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch {
        result = raw ? { message: raw } : {};
      }

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to load custom report");
      }

      const nextReport = {
        report_type: result?.report_type || reportType,
        report_title: result?.report_title || "",
        restaurant: result?.restaurant || "",
        fromdate: result?.fromdate || from,
        todate: result?.todate || to,
        row_count: Number(result?.row_count ?? 0),
        preview_count: Number(result?.preview_count ?? 0),
        columns: Array.isArray(result?.columns) ? result.columns : [],
        preview_rows: Array.isArray(result?.preview_rows) ? result.preview_rows : [],
        summary:
          result?.summary && typeof result.summary === "object" ? result.summary : {},
        generated_at: result?.generated_at || "",
      };

      setReport(nextReport);
      setLoadedOnce(true);

      if (includeFile) {
        if (!result?.file_base64) {
          throw new Error("Excel file not received from server");
        }
        downloadBase64File(
          result.file_base64,
          result?.file_name || `custom_report_${Date.now()}.xlsx`,
          result?.file_mime ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        );
      }
    } catch (error) {
      toast.error(error.message || "Unable to load custom report");
    } finally {
      if (includeFile) {
        setExporting(false);
      } else {
        setLoading(false);
      }
    }
  };

  const restaurant = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
    } catch {
      return {};
    }
  }, []);

  const restaurantName = report.restaurant || restaurant.restaurant_name || restaurant.restaurantname || restaurant.name || "Ivaan Fast Food";
  const restaurantAddress = restaurant.addressline3 || restaurant.address || restaurant.restaurantaddress || restaurant.city || "Kharar";
  const restaurantPhone = restaurant.mobile_number || restaurant.phone || restaurant.mobile || "9991000409";
  const generatedOn = useMemo(
    () =>
      new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <ReportPageHeader
        title="Custom Excel Report"
        subtitle="Preview report data and export the exact Excel file."
        stats={[
          { label: "Report Type", value: reportTypeOptions.find(o => o.value === reportType)?.label || "Order Wise" },
          { label: "Records", value: report?.row_count || 0 },
          { label: "Status", value: loadedOnce ? "Ready" : "Pending" },
        ]}
      />

      <ReportControlBar>
        <Controls style={{ width: "100%", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <ReportPeriodPicker fromDate={fromDate} toDate={toDate} onChange={({ from, to }) => { setFromDate(from); setToDate(to); }} />
            <Field>
              <select
                aria-label="Report type"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                {reportTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <PrimaryBtn type="button" onClick={() => fetchReport()}>
            <FaSearch /> {loading ? "Loading..." : "Preview"}
          </PrimaryBtn>
        </Controls>
      </ReportControlBar>

      <Card>
        <div ref={reportRef}>
          <PdfHeaderCard>
            <PdfHeaderRow>
              <div className="left">
                <div className="resto">{restaurantName}</div>
                {restaurantAddress && <div className="line">{restaurantAddress}</div>}
                {restaurantPhone && <div className="line">Phone: {restaurantPhone}</div>}
              </div>
              <div className="right">
                <h2>Custom Excel Report</h2>
                <div className="line">
                  Period: <strong>{fromDate}</strong> to <strong>{toDate}</strong>
                </div>
                <div className="line">
                  Report Type: <strong>{reportTypeOptions.find(o => o.value === reportType)?.label || "Order Wise"}</strong>
                </div>
                <div className="line">
                  Generated on: <strong>{generatedOn}</strong>
                </div>
              </div>
            </PdfHeaderRow>
          </PdfHeaderCard>

          {!loadedOnce && !loading ? (
            <Center>Select filters and generate preview.</Center>
          ) : loading ? (
            <Center>Loading preview…</Center>
          ) : (
            <>
              <MetaRow>
                <Pill>{report.report_title || "Custom Report"}</Pill>
                <Pill>
                  Range: {report.fromdate || fromDate} to {report.todate || toDate}
                </Pill>
                <Pill>Rows: {report.row_count}</Pill>
                <Pill>Preview: {report.preview_count}</Pill>
              </MetaRow>

              {summaryEntries.length ? (
                <SummaryGrid>
                  {summaryEntries.map(([key, value]) => (
                    <SummaryCard key={key}>
                      <SummaryLabel>{humanize(key)}</SummaryLabel>
                      <SummaryValue>{formatSummaryValue(value)}</SummaryValue>
                    </SummaryCard>
                  ))}
                </SummaryGrid>
              ) : null}

              {report.preview_rows.length ? (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        {normalizedColumns.map((column) => (
                          <th key={column.id}>{column.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {report.preview_rows.map((row, rowIndex) => (
                        <tr key={`row-${rowIndex}`}>
                          {normalizedColumns.map((column, colIndex) => (
                            <td key={`${column.id}-${colIndex}`}>
                              {column.id === "sno"
                                ? rowIndex + 1
                                : formatCell(row[column.id], column.id)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              ) : (
                <Center>No preview rows available.</Center>
              )}
            </>
          )}

          <PdfFooterRow style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, paddingTop: 12, borderTop: "1px dashed #cbd5e1" }}>
            <span style={{ color: "#64748b", fontSize: "0.9rem" }}>Powered by <strong>BillSarthi</strong></span>
            <PrimaryBtn type="button" onClick={() => fetchReport({ includeFile: true })}>
              <FaFileExcel /> {exporting ? "Exporting..." : "Export Excel"}
            </PrimaryBtn>
          </PdfFooterRow>
        </div>
      </Card>
    </Page>
  );
};

export default CustomExcelReportPage;

const downloadBase64File = (base64, fileName, mimeType) => {
  const byteCharacters = window.atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = String(fileName || "custom_report.xlsx").replace(/[^A-Za-z0-9._-]+/g, "_");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const humanize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatCell = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "number") {
    return value.toLocaleString("en-IN", {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }
  return String(value);
};

const today = localToday;

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom:16px;padding:26px 28px;border-radius:22px;background:linear-gradient(135deg,#075f9e,#0787c5);box-shadow:0 14px 30px rgba(1,91,154,.17);
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color:#fff;
`;

const Sub = styled.div`
  color:#dff3ff;
`;

const Controls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Field = styled.label`
  display: flex;
  align-items: center;

  select,
  input {
    min-width: 150px;
    min-height: 48px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid ${COLOR.line};
    background: #fff;
    outline: none;
  }
`;

const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  height: fit-content;
`;

const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
`;

const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 32px 16px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Pill = styled.span`
  background: #eef2ff;
  color: ${COLOR.text};
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
`;

const SummaryCard = styled.div`
  background: #f8fafc;
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
`;

const SummaryLabel = styled.div`
  font-size: 0.85rem;
  color: ${COLOR.muted};
  margin-bottom: 6px;
`;

const SummaryValue = styled.div`
  color: ${COLOR.text};
  font-size: 1.05rem;
  font-weight: 900;
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;

  th,
  td {
    border-bottom: 1px solid ${COLOR.line};
    padding: 12px;
    text-align: left;
    font-size: 0.95rem;
  }

  th {
    background: #eff6ff;
    color: ${COLOR.text};
    font-weight: 800;
  }
`;

const PdfHeaderCard = styled.div`
  background: linear-gradient(135deg, #f0f7ff, #ffffff);
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 14px;
`;

const PdfHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  .left,
  .right {
    display: grid;
    gap: 4px;
  }

  .resto {
    font-size: 1.15rem;
    font-weight: 900;
    color: ${COLOR.text};
  }

  .line {
    color: ${COLOR.muted};
    font-size: 0.95rem;
  }

  h2 {
    margin: 0;
    color: ${COLOR.text};
    font-size: 1.1rem;
  }
`;

const PdfFooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed #cbd5e1;
`;
